import React, { useEffect, useRef, useState } from "react";

import {
  faCheckCircle,
  faPaperPlane,
  faShare,
} from "@fortawesome/pro-solid-svg-icons";
import { Divider, Grid, Tab, Tabs } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";

import { EContractAction } from "actions";

import { PDFPreview } from "components/BillingWorkFlow/PDFPreview";
import { ConfirmDialog } from "components/ConfirmDialog";
import { SmallFaIcon } from "components/FaIcon";
import { DeleteButton, SecondaryButton } from "components/Form";
import { Row } from "components/Layout";
import { Dialog, DialogActions, DialogTitle } from "components/MaterialDialog";
import { Text } from "components/Text";

import { EContractParty } from "constants/eContract";
import { ModalAdapterProps, ModalTypes } from "constants/navigation";
import {
  DeploymentPermission,
  EContractPermission,
} from "constants/permissions";

import {
  getEContractCodeVersionAndStatusText,
  hasBeenSentToBuyer,
  hasBeenSentToVendor,
} from "lib/econtracts";
import { getReportJobUrl } from "lib/navigation";
import { hasPermission } from "lib/permissions";
import toast from "lib/toast";

import { getActiveRole, getEContractById } from "selectors";

import history from "appHistory";
import {
  useBoolean,
  useHasDeploymentPermission,
  useHasPermission,
  useIsMobile,
  useModalAdapter,
} from "hooks";
import { useEContractShare } from "hooks/useEContractShare";

import { EContractSendEmailModal } from "./sendEmailModal";
import { MultiButton } from "components/Button";
import { MultiButtonOption } from "components/Button/MultiButton/types";

interface EContractModalParams {
  id: string;
  readOnly?: boolean;
}

export const EContractModal = (
  props: ModalAdapterProps,
): React.JSX.Element | null => {
  const [hashParams, returnTo, onClose] = useModalAdapter<EContractModalParams>(
    ModalTypes.EContract,
    props,
  );
  const { id: eContractId, readOnly } = hashParams;

  const dispatch = useDispatch();
  const { slug: activeRoleSlug } = useSelector(getActiveRole);
  const eContract = useSelector(getEContractById(eContractId));

  const isMobile = useIsMobile();

  const hasFeaturePermission = useHasDeploymentPermission(
    DeploymentPermission.featureEContracts,
  );
  const hasReadPermission = useHasPermission(
    getEContractById(eContractId),
    EContractPermission.read,
  );
  const canVoid = hasPermission(eContract, EContractPermission.void);
  const canAccept = hasPermission(eContract, EContractPermission.accept);

  const { shareEContract, canShare } = useEContractShare(eContract?.id);

  const [isVoidDialogShowing, showVoidDialog, hideVoidDialog] =
    useBoolean(false);

  const [emailDialogParty, setEmailDialogParty] = useState(null);
  // Default to the vendor document, as it's the superset view (includes commission terms).
  const [previewParty, setPreviewParty] = useState(EContractParty.VENDOR);

  const buyerDocumentId = eContract?.buyerDocumentId;
  const vendorDocumentId = eContract?.vendorDocumentId;
  // Split docs only exist when split_econtract_documents was enabled for the
  // deployment at generation time.
  const isSplit = Boolean(buyerDocumentId || vendorDocumentId);

  const buyerSent = hasBeenSentToBuyer(eContract);
  const vendorSent = hasBeenSentToVendor(eContract);
  const buyerAccepted = Boolean(eContract?.buyerAcceptedOn);
  const vendorAccepted = Boolean(eContract?.vendorAcceptedOn);

  // Once a document has been sent, jump the preview over to whichever one
  // hasn't - so after sending to the buyer, the agent lands on the vendor
  // document next, rather than the one they just sent. Split contracts
  // only - a non-split contract shows both send buttons regardless of
  // preview tab, so switching would have no effect on what can be sent.

  useEffect(() => {
    if (!isSplit) {
      return;
    }
    // Reads the current tab via the setState updater (rather than closing
    // over previewParty directly) so a manual tab click isn't fought, without
    // needing to list previewParty as a dep.
    setPreviewParty(currentParty => {
      if (currentParty === EContractParty.BUYER && buyerSent && !vendorSent) {
        return EContractParty.VENDOR;
      }
      if (currentParty === EContractParty.VENDOR && vendorSent && !buyerSent) {
        return EContractParty.BUYER;
      }
      return currentParty;
    });
  }, [isSplit, buyerSent, vendorSent]);

  const bothSent = buyerSent && vendorSent;
  const wasBothSentRef = useRef(bothSent);

  // Nothing left to send once both have gone out as a split document - close the modal, but
  // only as a reaction to just finishing the second send this session, not
  // on opening a contract that was already fully sent.
  useEffect(() => {
    if (isSplit && bothSent && !wasBothSentRef.current) {
      onClose();
    }
    wasBothSentRef.current = bothSent;
  }, [isSplit, bothSent, onClose]);

  if (!hasFeaturePermission || !hasReadPermission) {
    // don't display model if feature permission missing
    toast.error("You are unable to view this eContract");
    if (returnTo) {
      history.push(returnTo);
    }
    return null;
  }

  const voidEContract = () => {
    dispatch(EContractAction.void({ id: eContract.id }));
    hideVoidDialog();
  };

  const { voidedOn, agentAcceptedOn, documentId } = eContract;
  const eContractStatus = getEContractCodeVersionAndStatusText(eContract);

  const previewDocumentId = isSplit
    ? previewParty === EContractParty.BUYER
      ? buyerDocumentId
      : vendorDocumentId
    : documentId;

  // add status onto url for force reloading pdf view when econtract status changes.
  // status query param is not used by backend, and url is otherwise static
  // adding this allows the PDF to refresh when voided, without closing and re-opening the modal.
  const reportJobUrl = getReportJobUrl(previewDocumentId, activeRoleSlug);
  const documentPreviewUrl = reportJobUrl
    ? `${reportJobUrl}&status=${eContractStatus}`
    : null;

  const buyerButtons: MultiButtonOption[] = [
    {
      title: `${agentAcceptedOn || voidedOn ? "" : "Accept and "} Email to Buyer`,
      onClick: () => setEmailDialogParty(EContractParty.BUYER),
      default: true,
      isDisabled: !canAccept,
    },
    canShare && {
      title: `${agentAcceptedOn || voidedOn ? "" : "Accept and "} Share to Buyer`,
      onClick: () => shareEContract(EContractParty.BUYER),
      isDisabled: !canAccept,
      icon: faShare,
    },
  ].filter(Boolean);

  const vendorButtons: MultiButtonOption[] = [
    {
      title: `${agentAcceptedOn || voidedOn ? "" : "Accept and "} Email to Vendor`,
      onClick: () => setEmailDialogParty(EContractParty.VENDOR),
      default: true,
      isDisabled: !canAccept,
    },
    canShare && {
      title: `${agentAcceptedOn || voidedOn ? "" : "Accept and "} Share to Vendor`,
      onClick: () => shareEContract(EContractParty.VENDOR),
      isDisabled: !canAccept,
      icon: faShare,
    },
  ].filter(Boolean);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xl"
      height="100%"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle onClose={onClose}>eContract - {eContractStatus}</DialogTitle>
      <Divider />
      {isSplit ? (
        <>
          {!readOnly && (
            <Text color="titleMedium" marginHorizontal={2} marginVertical={2}>
              The Vendor Document will be sent to the Vendor, Buyer Document
              will be sent to the Buyer.
            </Text>
          )}

          <Tabs
            value={previewParty}
            onChange={(_event, newValue) => setPreviewParty(newValue)}
            indicatorColor="primary"
          >
            <Tab
              label={
                <Row alignCenter gridGap={1}>
                  Vendor Document
                  {vendorAccepted ? (
                    <SmallFaIcon color="success" icon={faCheckCircle} />
                  ) : (
                    vendorSent && (
                      <SmallFaIcon color="inProgress" icon={faPaperPlane} />
                    )
                  )}
                </Row>
              }
              value={EContractParty.VENDOR}
            />
            <Tab
              label={
                <Row alignCenter gridGap={1}>
                  Buyer Document
                  {buyerAccepted ? (
                    <SmallFaIcon color="success" icon={faCheckCircle} />
                  ) : (
                    buyerSent && (
                      <SmallFaIcon color="inProgress" icon={faPaperPlane} />
                    )
                  )}
                </Row>
              }
              value={EContractParty.BUYER}
            />
          </Tabs>
        </>
      ) : null}
      <PDFPreview url={documentPreviewUrl} />
      <DialogActions>
        {readOnly ? (
          <SecondaryButton type="button" onClick={onClose}>
            Close
          </SecondaryButton>
        ) : (
          <Grid
            container
            item
            xs={12}
            alignItems="center"
            justifyContent="center"
            spacing={1}
          >
            <Grid item xs={12} sm={4}>
              <DeleteButton
                disabled={voidedOn || !canVoid}
                onClick={showVoidDialog}
                fullWidth
                margin={0}
              >
                Void eContract
              </DeleteButton>
            </Grid>
            {(!isSplit || previewParty === EContractParty.BUYER) && (
              <Grid item xs={12} sm={4}>
                <MultiButton
                  buttons={buyerButtons}
                  fullWidth
                  buttonInline={false}
                />
              </Grid>
            )}
            {(!isSplit || previewParty === EContractParty.VENDOR) && (
              <Grid item xs={12} sm={4}>
                <MultiButton
                  buttons={vendorButtons}
                  fullWidth
                  buttonInline={false}
                />
              </Grid>
            )}
          </Grid>
        )}
      </DialogActions>
      {isVoidDialogShowing ? (
        <ConfirmDialog
          buttonMessage="Void"
          isOpen={isVoidDialogShowing}
          onCancel={hideVoidDialog}
          onDelete={voidEContract}
          title="Confirm you want to void this eContract"
        />
      ) : null}
      {emailDialogParty ? (
        <EContractSendEmailModal
          eContractId={eContractId}
          onClose={() => setEmailDialogParty(null)}
          party={emailDialogParty}
        />
      ) : null}
    </Dialog>
  );
};
